import { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import * as Database from "utils/database.utils";
import WeightHistoryChartContainer from "./WeightHistoryChart.styles";
import colors from "assets/styles/colors";

interface HistoryPoint {
    date: string;
    weight: number | null;
}

interface WeightHistoryChartProps {
    bbgTicker: string;
    companyId: string;
    companyName: string;
    days?: number;
}

interface ChartDataState {
    portfolioData: HistoryPoint[];
    teamData: HistoryPoint[];
}

// Aligns two sparse time series onto a single sorted date axis
const mergeOnDates = (
    portfolioData: HistoryPoint[],
    teamData: HistoryPoint[]
): {
    dates: string[];
    portfolioWeights: (number | null)[];
    teamWeights: (number | null)[];
} => {
    const dateSet = new Set<string>();
    const portfolioMap = new Map<string, number | null>();
    const teamMap = new Map<string, number | null>();

    for (const p of portfolioData) {
        dateSet.add(p.date);
        portfolioMap.set(p.date, p.weight);
    }
    for (const t of teamData) {
        dateSet.add(t.date);
        teamMap.set(t.date, t.weight);
    }

    const dates = Array.from(dateSet).sort();
    const portfolioWeights = dates.map((d) =>
        portfolioMap.has(d) && portfolioMap.get(d) !== null
            ? (portfolioMap.get(d) as number) * 100
            : null
    );
    const teamWeights = dates.map((d) =>
        teamMap.has(d) && teamMap.get(d) !== null
            ? (teamMap.get(d) as number) * 100
            : null
    );

    return { dates, portfolioWeights, teamWeights };
};

const WeightHistoryChart: React.FC<WeightHistoryChartProps> = ({
    bbgTicker,
    companyId,
    companyName,
    days = 365,
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [chartData, setChartData] = useState<ChartDataState | null>(null);
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setChartData(null);

        const fetchHistory = async () => {
            try {
                const [portfolioRes, teamRes] = await Promise.all([
                    Database.get(
                        `api/team-dummy-portfolios/portfolio-weight-history/${bbgTicker}?days=${days}`
                    ),
                    Database.get(
                        `api/team-dummy-portfolios/team-weight-history/${companyId}?days=${days}`
                    ),
                ]);

                const portfolioData: HistoryPoint[] = portfolioRes.ok
                    ? await portfolioRes.json()
                    : [];
                const teamData: HistoryPoint[] = teamRes.ok
                    ? await teamRes.json()
                    : [];

                if (portfolioData.length === 0 && teamData.length === 0) {
                    setError("No historical data available");
                } else {
                    setChartData({ portfolioData, teamData });
                }
            } catch (err) {
                setError("Error loading chart");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [bbgTicker, companyId, days]);

    useEffect(() => {
        if (!chartData || loading || !chartRef.current) return;

        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const ctx = chartRef.current.getContext("2d");
        if (!ctx) return;

        const { dates, portfolioWeights, teamWeights } = mergeOnDates(
            chartData.portfolioData,
            chartData.teamData
        );

        const datasets = [];

        if (chartData.portfolioData.length > 0) {
            datasets.push({
                label: "Portfolio Weight",
                data: portfolioWeights,
                borderColor: colors.textTeal,
                borderWidth: 2,
                tension: 0.1,
                pointRadius: 2,
                spanGaps: true,
            });
        }

        if (chartData.teamData.length > 0) {
            datasets.push({
                label: "Team Weight",
                data: teamWeights,
                borderColor: colors.accentOrange,
                borderWidth: 2,
                tension: 0.1,
                pointRadius: 2,
                spanGaps: true,
            });
        }

        chartInstance.current = new Chart(ctx, {
            type: "line",
            data: { labels: dates, datasets },
            options: {
                maintainAspectRatio: false,
                interaction: {
                    axis: "x",
                    intersect: false,
                },
                scales: {
                    y: {
                        title: { display: true, text: "Weight (%)" },
                        ticks: {
                            callback: (value) => `${parseFloat(Number(value).toFixed(1))}%`,
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: datasets.length > 1,
                        labels: {
                            usePointStyle: true,
                            pointStyle: "line",
                        },
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || "";
                                return `${label}: ${context.parsed.y.toFixed(
                                    1
                                )}%`;
                            },
                        },
                    },
                },
            },
        });

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
        };
    }, [chartData, loading]);

    return (
        <WeightHistoryChartContainer>
            <div className="chart-header">
                <h4>{companyName}</h4>
                <span className="chart-subtitle">
                    Weight History (Last {days} days)
                </span>
            </div>
            <div className="chart-body">
                {loading && <div className="loading">Loading chart...</div>}
                {error && <div className="error">{error}</div>}
                {!loading && !error && (
                    <div className="chart-container">
                        <canvas ref={chartRef}></canvas>
                    </div>
                )}
            </div>
        </WeightHistoryChartContainer>
    );
};

export default WeightHistoryChart;
